import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import {
  AdminBlogShell,
  AdminMemberEditorFields,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { LoadingSpinner } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  fetchYowanAdminMemberDetail,
  updateYowanAdminMember,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const AdminMembersEditPage = () => {
  const content = yowanPageContent['/admin/members/:memberId/edit'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const navigate = useNavigate();
  const { memberId } = useParams();
  const [currentRecord, setCurrentRecord] = useState(null);
  const [submitMessage, setSubmitMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues:
      currentRecord || {
        name: '',
        nameKana: '',
        email: '',
        lifecycleStatus: 'active',
        phone: '',
        postalCode: '',
        address: '',
        note: '',
      },
  });

  useEffect(() => {
    if (!content || !memberId) {
      return undefined;
    }

    let isMounted = true;

    const loadMember = async () => {
      setIsLoading(true);
      setLoadError('');

      try {
        const record = await fetchYowanAdminMemberDetail(memberId);

        if (!isMounted) {
          return;
        }

        setCurrentRecord(record);
        reset(record);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setCurrentRecord(null);
        setLoadError(content.form.loadErrorMessage);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadMember();

    return () => {
      isMounted = false;
    };
  }, [content, memberId, reset]);

  if (!content) {
    return null;
  }

  const onSubmit = async (values) => {
    setIsSubmitting(true);
    setSubmitMessage(content.form.pendingMessage);

    try {
      await updateYowanAdminMember(memberId, values);
      setSubmitMessage(content.form.successMessage);
      navigate(appendYowanPreviewQueryIfNeeded('/admin/members', preservedDomainSearch));
    } catch (error) {
      setSubmitMessage(content.form.errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };
  const buildEventProfileHref = (baseCreateHref, baseEditHref, profile) => {
    const searchParams = new URLSearchParams(preservedDomainSearch);

    if (currentRecord?.id) {
      searchParams.set('memberId', currentRecord.id);
      searchParams.set('memberLabel', `${currentRecord.name || '氏名未設定'} / ${currentRecord.email || '-'}`);
    }

    if (profile?.id) {
      return appendYowanPreviewQueryIfNeeded(`${baseEditHref}/${profile.id}/edit`, preservedDomainSearch);
    }

    const queryString = searchParams.toString();
    return `${baseCreateHref}${queryString ? `?${queryString}` : ''}`;
  };

  return (
    <AdminBlogShell activeNavHref="/admin/members" routeKey="/admin/members/:memberId/edit">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="back-to-list" to={appendYowanPreviewQueryIfNeeded('/admin/members', preservedDomainSearch)}>
            一覧へ戻る
          </Link>,
          <Link key="member-access" to={appendYowanPreviewQueryIfNeeded('/admin/access/member', preservedDomainSearch)}>
            会員アクセス一覧へ
          </Link>,
          <Link key="operator-access" to={appendYowanPreviewQueryIfNeeded('/admin/access/operator', preservedDomainSearch)}>
            管理権限一覧へ
          </Link>,
          currentRecord ? (
            <Link
              key="instructor-profile"
              to={buildEventProfileHref(
                '/admin/events/instructors/new',
                '/admin/events/instructors',
                currentRecord.instructorProfile,
              )}
            >
              {currentRecord.instructorProfile?.id ? '講師プロフィール編集' : '講師プロフィール作成'}
            </Link>
          ) : null,
          currentRecord ? (
            <Link
              key="organizer-profile"
              to={buildEventProfileHref(
                '/admin/events/organizers/new',
                '/admin/events/organizers',
                currentRecord.organizerProfile,
              )}
            >
              {currentRecord.organizerProfile?.id ? '主催者プロフィール編集' : '主催者プロフィール作成'}
            </Link>
          ) : null,
        ].filter(Boolean)}
      />

      {isLoading || loadError || !currentRecord ? (
        <div className="yowan-admin-form c-surface-content">
          {isLoading ? <LoadingSpinner size="sm" /> : loadError || content.form.loadErrorMessage}
        </div>
      ) : (
        <form className="yowan-admin-form c-surface-content" onSubmit={handleSubmit(onSubmit)}>
          <AdminMemberEditorFields content={content} register={register} errors={errors} />

          <div className="yowan-admin-form__actions">
            <button type="submit" className="c-form__submit" disabled={isSubmitting}>
              {isSubmitting ? content.form.pendingMessage : content.form.submitLabel}
            </button>
          </div>

          {submitMessage ? <p className="yowan-admin-form__message">{submitMessage}</p> : null}
        </form>
      )}
    </AdminBlogShell>
  );
};

export default AdminMembersEditPage;
